import { useEffect, useMemo, useState } from 'react';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { HomePageWidgetBlueprint } from '@backstage/plugin-home-react/alpha';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { Table, Cell, CellText, Link, Text, type ColumnConfig } from '@backstage/ui';
import { useEntityRefLink } from '@backstage/plugin-catalog-react';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import WarningOutlinedIcon from '@material-ui/icons/WarningOutlined';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { getCertificateHealth, type CertificateHealth } from './status';
import './widget.css';

type Certificate = {
  name: string;
  endpoint: string;
  status: 'valid' | 'expired' | 'invalid' | 'unreachable' | 'not-configured';
  validFrom?: string;
  validUntil?: string;
  subjectNames?: string[];
  issuer?: string;
  error?: string;
};

type CertificateRow = Certificate & { id: string };

function StatusIcon({ color }: { color: CertificateHealth['color'] }) {
  const Icon = color === 'success' ? CheckCircleOutlineIcon
    : color === 'warning' ? WarningOutlinedIcon
    : color === 'danger' ? ErrorOutlineIcon : HelpOutlineIcon;
  return <Icon fontSize="small" aria-hidden="true" style={{ color: `var(--bui-fg-${color})` }} />;
}

const createColumns = (entityLink: ReturnType<typeof useEntityRefLink>): ColumnConfig<CertificateRow>[] => [
  { id: 'name', label: 'Certificate', isRowHeader: true,
    cell: item => <Cell>
      <Text as="p" style={{ overflowWrap: 'anywhere' }}>
        <Link href={entityLink(`resource:${item.name}`)}>{item.name}</Link>
      </Text>
      <Text as="p" color="secondary" style={{ overflowWrap: 'anywhere' }}>{item.endpoint}</Text>
    </Cell> },
  { id: 'status', label: 'Status', cell: item => {
    const health = getCertificateHealth(item);
    return <CellText
      title={health.label}
      description={item.error}
      leadingIcon={<StatusIcon color={health.color} />}
    />;
  } },
  { id: 'validFrom', label: 'Valid from',
    cell: item => <CellText title={item.validFrom?.slice(0, 10) ?? '—'} /> },
  { id: 'validUntil', label: 'Valid until',
    cell: item => <CellText title={item.validUntil?.slice(0, 10) ?? '—'} /> },
  { id: 'subjectNames', label: 'Subject names',
    cell: item => <Cell><Text as="span" style={{ overflowWrap: 'anywhere' }}>
      {item.subjectNames?.join(', ') || '—'}
    </Text></Cell> },
  { id: 'issuer', label: 'Issuer',
    cell: item => <CellText title={item.issuer ?? '—'} /> },
];

function CertificatesWidget() {
  const entityLink = useEntityRefLink();
  const columns = useMemo(() => createColumns(entityLink), [entityLink]);
  const discovery = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [items, setItems] = useState<Certificate[]>([]);
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const base = await discovery.getBaseUrl('certificates');
        const response = await fetchApi.fetch(`${base}/status`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = (await response.json()) as { items: Certificate[] };
        if (active) {
          setItems(data.items);
          setError(undefined);
        }
      } catch (e) {
        if (active) setError((e as Error).message);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    const interval = setInterval(load, 5 * 60 * 1000);
    return () => { active = false; clearInterval(interval); };
  }, [discovery, fetchApi]);

  if (loading) return <Text>Loading certificates…</Text>;
  if (error) return <Text color="danger">Could not load certificates: {error}</Text>;
  if (!items.length) return <Text>No tls-certificate resources found.</Text>;
  return (
    <div className="certificates-widget">
      <div className="certificates-widget__table">
        <Table columnConfig={columns} data={items.map(item => ({ ...item, id: item.name }))} pagination={{ type: 'none' }} />
      </div>
      <div className="certificates-widget__list" aria-label="TLS certificates">
        {items.map(item => {
          const health = getCertificateHealth(item);
          return (<section className="certificates-widget__item" key={item.name}>
            <Text as="p" weight="bold" style={{ overflowWrap: 'anywhere' }}>
              <Link href={entityLink(`resource:${item.name}`)}>{item.name}</Link>
            </Text>
            <Text as="p" color="secondary" style={{ overflowWrap: 'anywhere' }}>{item.endpoint}</Text>
            <dl className="certificates-widget__details">
              <dt>Status</dt><dd>
                <span className="certificates-widget__status" style={{ color: `var(--bui-fg-${health.color})` }}>
                  <StatusIcon color={health.color} />
                  <Text as="span" color={health.color}>{health.label}</Text>
                </span>
                {item.error && <Text as="p" color="secondary">{item.error}</Text>}
              </dd>
              <dt>Valid from</dt><dd>{item.validFrom?.slice(0, 10) ?? '—'}</dd>
              <dt>Valid until</dt><dd>{item.validUntil?.slice(0, 10) ?? '—'}</dd>
              <dt>Subject names</dt><dd>{item.subjectNames?.join(', ') || '—'}</dd>
              <dt>Issuer</dt><dd>{item.issuer ?? '—'}</dd>
            </dl>
          </section>);
        })}
      </div>
    </div>
  );
}

const certificatesWidget = HomePageWidgetBlueprint.make({
  name: 'certificates',
  params: {
    name: 'Certificates',
    title: 'TLS Certificates',
    description: 'Live certificate health for catalog tls-certificate resources',
    components: async () => ({ Content: CertificatesWidget }),
  },
});

export default createFrontendModule({
  // This is a module of the Home plugin, not a standalone page plugin.
  pluginId: 'home',
  extensions: [certificatesWidget],
});
